/**
 * Organization hooks: the current org and the branding
 * (accent/typeface/logo) mutations.
 */

import { hubQueryKeys, type OrgBrandingPayload } from "@lifter/hub-ui/browser-shared";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHubApi } from "./context";

/**
 * Branding is read twice: the authed org record the settings form edits, and
 * the public instance payload the white-label cascade and the document title
 * follow. Refreshing only the first leaves the tab, the accent and the tab icon
 * describing the previous branding until the next full page load.
 */
function refreshBranding(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.orgCurrent() });
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.instanceBranding() });
}

export function useCurrentOrgQuery() {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.orgCurrent(),
    queryFn: () => api.org.current(),
  });
}

export function useUpdateBrandingMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrgBrandingPayload) => api.org.updateBranding(payload),
    onSuccess: () => {
      refreshBranding(queryClient);
    },
  });
}

// Org logo upload/clear lives outside the JSON PATCH form because the upload
// is a multipart file and the bytes round-trip through R2 before we know the
// public URL.
export function useUploadOrgLogoMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.org.uploadLogo(file),
    onSuccess: () => {
      refreshBranding(queryClient);
    },
  });
}

export function useDeleteOrgLogoMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.org.deleteLogo(),
    onSuccess: () => {
      refreshBranding(queryClient);
    },
  });
}

export function useUpdateOrgSecurityMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.org.updateSecurity(enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: hubQueryKeys.orgCurrent() }),
  });
}
